<script setup>
console.log('🚀 GM KPI Card: Initializing KPI card component...');

import { computed } from 'vue';

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  value: {
    type: [String, Number],
    required: true
  },
  description: {
    type: String,
    default: ''
  },
  color: {
    type: String,
    default: '#3b82f6'
  },
  icon: {
    type: String,
    default: 'icon-kpi'
  },
  trend: {
    type: Object,
    default: null
  },
  target: {
    type: [String, Number],
    default: null
  }
});

console.log('📊 GM KPI Card: Props received:', {
  title: props.title,
  value: props.value,
  description: props.description,
  color: props.color,
  icon: props.icon,
  hasTarget: !!props.target
});

// Computed properties
const hasTrend = computed(() => {
  const hasTrendData = props.trend && (props.trend.value !== undefined || props.trend.percentage !== undefined);
  console.log('📈 GM KPI Card: Has trend data:', hasTrendData);
  return hasTrendData;
});

const hasTarget = computed(() => {
  const hasTargetValue = props.target !== null && props.target !== undefined;
  console.log('🎯 GM KPI Card: Has target value:', hasTargetValue);
  return hasTargetValue;
});

const trendIcon = computed(() => {
  if (!hasTrend.value) return '';
  
  const trendValue = props.trend.value || props.trend.percentage || 0;
  const isPositive = trendValue > 0;
  const isNeutral = trendValue === 0;
  
  console.log('📈 GM KPI Card: Trend value:', trendValue, 'Is positive:', isPositive);
  
  if (isNeutral) return 'icon-trend-neutral';
  return isPositive ? 'icon-trend-up' : 'icon-trend-down';
});

const trendColor = computed(() => {
  if (!hasTrend.value) return props.color;
  
  const trendValue = props.trend.value || props.trend.percentage || 0;
  const isPositive = trendValue > 0;
  const isNeutral = trendValue === 0;
  
  if (isNeutral) return '#64748b';
  return isPositive ? '#10b981' : '#ef4444';
});

const formattedValue = computed(() => {
  if (typeof props.value === 'number') {
    return props.value.toLocaleString();
  }
  return props.value;
});

const targetAchievement = computed(() => {
  if (!hasTarget.value) return null;
  
  const currentValue = typeof props.value === 'number' ? props.value : parseFloat(props.value) || 0;
  const targetValue = typeof props.target === 'number' ? props.target : parseFloat(props.target) || 0;
  
  if (targetValue === 0) return null;
  
  const achievement = (currentValue / targetValue) * 100;
  console.log('🎯 GM KPI Card: Target achievement:', achievement + '%');
  
  return {
    percentage: Math.min(achievement, 100),
    isOverTarget: achievement > 100
  };
});

console.log('✅ GM KPI Card: Component setup completed');
</script>

<template>
  <div class="gm-kpi-card" :style="{ '--kpi-color': color }">
    <div class="gm-kpi-header">
      <div class="gm-kpi-icon">
        {{ icon }}
      </div>
      <div class="gm-kpi-title">
        {{ title }}
      </div>
    </div>
    
    <div class="gm-kpi-content">
      <div class="gm-kpi-value">
        {{ formattedValue }}
      </div>
      
      <div v-if="description" class="gm-kpi-description">
        {{ description }}
      </div>
      
      <!-- Target Progress Bar -->
      <div v-if="hasTarget" class="gm-kpi-target">
        <div class="gm-target-header">
          <span class="gm-target-label">Target</span>
          <span class="gm-target-value">{{ target }}</span>
        </div>
        <div class="gm-progress-bar">
          <div 
            class="gm-progress-fill" 
            :style="{ 
              width: `${targetAchievement.percentage}%`,
              backgroundColor: targetAchievement.isOverTarget ? '#10b981' : color
            }"
          ></div>
        </div>
        <div class="gm-achievement">
          {{ targetAchievement.percentage.toFixed(1) }}% of target
          <span v-if="targetAchievement.isOverTarget" class="gm-over-target">
            ({{ (targetAchievement.percentage - 100).toFixed(1) }}% over)
          </span>
        </div>
      </div>
      
      <!-- Trend Indicator -->
      <div v-if="hasTrend" class="gm-kpi-trend">
        <span class="gm-trend-icon" :style="{ color: trendColor }">
          {{ trend.value > 0 ? '↗️' : trend.value < 0 ? '↘️' : '➡️' }}
        </span>
        <span :style="{ color: trendColor }">
          {{ trend.percentage ? `${Math.abs(trend.percentage)}%` : Math.abs(trend.value) }}
        </span>
        <span class="gm-trend-period">
          {{ trend.period || 'vs last period' }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gm-kpi-card {
  background: white;
  border-radius: 10px;
  padding: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.gm-kpi-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-color: var(--kpi-color);
}

.gm-kpi-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.gm-kpi-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, var(--kpi-color), var(--kpi-color)dd);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.gm-kpi-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  line-height: 1.3;
  flex: 1;
}

.gm-kpi-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.gm-kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.gm-kpi-description {
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.4;
  margin-bottom: 0.5rem;
}

.gm-kpi-target {
  background: #f9fafb;
  border-radius: 8px;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  margin-top: auto;
}

.gm-target-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.gm-target-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.gm-target-value {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
}

.gm-progress-bar {
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.gm-progress-fill {
  height: 100%;
  background: var(--kpi-color);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.gm-achievement {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  text-align: center;
}

.gm-over-target {
  color: #10b981;
  font-weight: 600;
}

.gm-kpi-trend {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  margin-top: auto;
}

.gm-trend-icon {
  font-size: 0.875rem;
}

.gm-trend-period {
  color: #6b7280;
  font-weight: 400;
  font-size: 0.75rem;
}

.gm-kpi-card::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--kpi-color)08, transparent);
  border-radius: 0 12px 0 60px;
  pointer-events: none;
}

@media (max-width: 768px) {
  .gm-kpi-card {
    padding: 1rem;
  }
  
  .gm-kpi-value {
    font-size: 1.75rem;
  }
  
  .gm-kpi-icon {
    width: 36px;
    height: 36px;
    font-size: 1.125rem;
  }
}
</style>
